import React, {useMemo} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {get, useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckDouble} from "@fortawesome/free-solid-svg-icons";

// Utils
import {defaultUnacceptableParameterPrompt} from "../../utils/builder.js";

// Components
import InputRadio from "./InputRadio.js";
import InputTextArea from "./InputTextArea.js";
import InputError from "./InputError.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {pad} from "../../style/components/variables.js";
import {
  FormFieldWrapper,
  Label,
  Input,
  Text,
  Pill,
  Abbr,
  Error
} from "../../style/components/general.js";

const RadioGroupInput = ({
  testId = "input-radio-group",
  name,
  label,
  help,
  options = [],
  other = false,
  required = false,
  disabled = false,
  orient = false,
  prompt = "",
  defaultValue,
  globalUnacceptable,
  globalUnacceptableTitles,
  hasAlert = false,
  alertMessage,
  alertCondition,
  condition,
  tag,
  shared = false,
  linkedAcrossStates = false
}) => {
  const {
    watch,
    setValue,
    formState: {errors}
  } = useFormContext();

  const values = watch(name);

  const sanitizeValue = option => {
    if (option?.value) return option.value;
    if (option?.name) return option.name;
    if (option?.option) return option.option;
    if (option?.label) return option.label;
    return option;
  };

  const sanitizeLabel = option => {
    if (option?.label) return option.label;
    if (option?.option) return option.option;
    if (option?.value) return option.value;
    return option;
  };

  const hasYupErrors = useMemo(() => {
    const {message} = get(errors, name) || {};
    return !!message;
  }, [errors, name]);

  return (
    <>
      <FormFieldWrapper data-testid={testId} standard>
        {label && (
          <FieldLabel htmlFor={name} data-testid={`${testId}-label`} bold inline>
            {globalUnacceptable}
            {label.toUpperCase()}
            {help && <Help>{help}</Help>}
            {required && "*"}
            {tag && (
              <Pill quiet>
                <Abbr title={tag}>{tag}</Abbr>
              </Pill>
            )}
            <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
            {condition && <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>}
          </FieldLabel>
        )}
        {prompt && <Text quiet>{prompt}</Text>}
        <OptionsWrapper orient={orient ? "column" : "row"} data-testid={`${testId}-wrapper`}>
          {options?.length > 0 &&
            options.map((option, index) => {
              const sanitizedLabel = sanitizeLabel(option);
              const sanitizedValue = sanitizeValue(option);
              const iconColor = option.iconColor || option.color;
              const {labelColor} = option;
              if (defaultValue && sanitizedValue === defaultValue) {
                return (
                  <InputRadio
                    key={sanitizedValue}
                    testId={`${testId}-option${index + 1}`}
                    name={name}
                    label={sanitizedLabel}
                    icon={option.icon}
                    value={sanitizedValue}
                    iconColor={iconColor}
                    labelColor={labelColor}
                    disabled={disabled}
                    showError={false}
                    defaultChecked
                  />
                );
              }
              return (
                <InputRadio
                  key={sanitizedValue}
                  testId={`${testId}-option${index + 1}`}
                  name={name}
                  label={sanitizedLabel}
                  icon={option.icon}
                  value={sanitizedValue}
                  iconColor={iconColor}
                  labelColor={labelColor}
                  disabled={disabled}
                  showError={false}
                />
              );
            })}

          {other && (
            <InputRadio
              testId={`${testId}-other`}
              name={name}
              disabled={disabled}
              label="Other"
              value="Other"
            />
          )}
        </OptionsWrapper>

        <InputError errors={errors} name={name} />

        {!hasYupErrors && globalUnacceptableTitles?.length && (
          <Error>
            Parameter violation{globalUnacceptableTitles.length > 1 ? "s" : ""}:{" "}
            {globalUnacceptableTitles.join(", ")}
          </Error>
        )}
      </FormFieldWrapper>

      {values?.includes("Other") && (
        <>
          <Other
            data-testid={`${testId}-other-text-input`}
            type="text"
            name={`${name}_other`}
            placeholder="Provide value..."
            disabled={disabled}
            onChange={e => {
              if (disabled) return;
              setValue(name, `Other: ${e.target.value}`);
              // Keep radio input checked
              document.querySelector(`[data-testid="${testId}-other-input"]`).checked = true;
            }}
            required
          />
          <InputError errors={errors} name={`${name}_other`} />
        </>
      )}

      {hasAlert &&
        ((Array.isArray(alertCondition) && alertCondition?.includes(values)) ||
          values === alertCondition) && (
          <>
            <InputTextArea
              name={`${name}_comment`}
              label={alertMessage || defaultUnacceptableParameterPrompt}
              required
              maxLength={1000}
              disabled={disabled}
            />
            <InputError errors={errors} name={`${name}_comment`} />
          </>
        )}
    </>
  );
};

RadioGroupInput.propTypes = {
  testId: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  label: PropTypes.string,
  help: PropTypes.string,
  other: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  orient: PropTypes.bool,
  prompt: PropTypes.string,
  defaultValue: PropTypes.string,
  globalUnacceptable: PropTypes.node,
  globalUnacceptableTitles: PropTypes.arrayOf(PropTypes.string),
  hasAlert: PropTypes.bool,
  alertMessage: PropTypes.string,
  alertCondition: PropTypes.any,
  condition: PropTypes.string,
  tag: PropTypes.string,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool
};

// Style Overrides
const FieldLabel = styled(Label)`
  white-space: normal;
  gap: ${pad / 2}px;
  margin-bottom: 2px;
`;

const Other = styled(Input)`
  margin-top: ${pad}px;
  max-width: 300px;
`;

const OptionsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;

  div {
    margin-top: 0;
  }

  ${({orient}) =>
    orient &&
    css`
      flex-direction: ${orient};
    `}
`;

export default RadioGroupInput;
